import { BuildsTable } from '@/features/builds/components/BuildsTable';
import { PipelinesTable } from '@/features/pipelines/components/PipelinesTable';
import { mergeFilters, type GridFilters } from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import { DeltasToggle } from '@/ui/composites/table';
import { Stack } from '@/ui/layout/Stack';
import { Box } from '@/ui/primitives';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { PipelineListItemDto, QueryResult } from '@flowqa/shared';
import type { ReactElement } from 'react';

type PipelinesDataPanelsProps = {
  tabsContentGap: 'md';
  activeTab: 'pipelines' | 'builds';
  onActiveTabChange: (nextTab: 'pipelines' | 'builds') => void;
  deltasEnabled: boolean;
  isLatestMode: boolean;
  isAllMode: boolean;
  tableDeltasEnabled: boolean;
  onTableDeltasEnabledChange: (enabled: boolean) => void;
  pipelinesQueryKey: readonly unknown[];
  pipelinesQueryFn: (input: GridQueryInput) => Promise<QueryResult<PipelineListItemDto>>;
  pipelineSearchFilters: GridFilters;
  pipelineStatusFilters: GridFilters;
  buildTableFilters: GridFilters;
  pipelinesNoRowsText?: string;
  buildsNoRowsText?: string;
};

export const PipelinesDataPanels = ({
  tabsContentGap,
  activeTab,
  onActiveTabChange,
  deltasEnabled,
  isLatestMode,
  isAllMode,
  tableDeltasEnabled,
  onTableDeltasEnabledChange,
  pipelinesQueryKey,
  pipelinesQueryFn,
  pipelineSearchFilters,
  pipelineStatusFilters,
  buildTableFilters,
  pipelinesNoRowsText,
  buildsNoRowsText,
}: PipelinesDataPanelsProps): ReactElement => {
  return (
    <Box>
      <Panel padding="md" gap="none" contentGap="md" sx={{ backgroundColor: 'background.paper' }}>
        <Stack gap={tabsContentGap}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" align="center" justify="space-between" sx={{ minWidth: 0 }}>
              <Tabs
                value={activeTab}
                onChange={(_, next) => {
                  onActiveTabChange(next as 'pipelines' | 'builds');
                }}
                aria-label="Pipelines page tabs"
              >
                <Tab label="Pipelines" value="pipelines" />
                <Tab label="Builds" value="builds" />
              </Tabs>
              {deltasEnabled && activeTab === 'pipelines' && !isLatestMode && !isAllMode ? (
                <DeltasToggle checked={tableDeltasEnabled} onChange={onTableDeltasEnabledChange} />
              ) : null}
            </Stack>
          </Box>
          {activeTab === 'pipelines' ? (
            <PipelinesTable
              persistKey="pipelines-table-v2"
              queryKey={pipelinesQueryKey}
              queryFn={pipelinesQueryFn}
              pageSizeOptions={[10]}
              initialPageSize={10}
              disablePageSizeSelect
              showDeltas={deltasEnabled && tableDeltasEnabled && !isLatestMode && !isAllMode}
              fixedFilters={mergeFilters(pipelineSearchFilters, pipelineStatusFilters)}
              noRowsText={pipelinesNoRowsText}
            />
          ) : (
            <BuildsTable
              persistKey="builds-table"
              pageSizeOptions={[10]}
              initialPageSize={10}
              disablePageSizeSelect
              fixedFilters={buildTableFilters}
              noRowsText={buildsNoRowsText}
            />
          )}
        </Stack>
      </Panel>
    </Box>
  );
};
